import { Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { And, Or, Rule, RuleInterface, StringEquals, StringListContains } from '@vendasta/automata';
import {
  FormControl,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { CommonTriggerPanelComponent } from '../../../../common/common-trigger-panel.component';
import {
  changeTypeVariablePath,
  CREATED,
  CrmCreatedModifiedSelectorService,
  fieldsChangedVariablePath,
  UPDATED,
} from '../../../crm-common/crm-created-modified-selector/crm-created-modified-selector.service';
import {
  ActionComparison,
  CrmCreatedModifiedSelectorComponent,
} from '../../../crm-common/crm-created-modified-selector/crm-created-modified-selector.component';
import { WORKFLOW_TRIGGERS } from '../../../../../component-loader/trigger-definitions';
import { CRMCustomObjectCreatedOrModified } from '../../../../../component-loader/constants';
import { AutomationReplacementChipsComponent } from '../../../../../../common/components';
import { supportedSelectionFieldTypes } from '../../../../filters';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { CrmCustomObjectTypeSelectorService } from '../../../../../../data-services/crm-custom-object-type-selector.service';
import { CrmFieldsSelectorService } from '../../../../../../data-services/crm-fields-selector.service';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'automata-crm-custom-object-created-modified-panel',
  imports: [
    CommonModule,
    TranslateModule,
    CrmCreatedModifiedSelectorComponent,
    AutomationReplacementChipsComponent,
    ReactiveFormsModule,
    GalaxyFormFieldModule,
  ],
  templateUrl: './crm-custom-object-created-modified-panel.component.html',
  styleUrls: ['./crm-custom-object-created-modified-panel.component.scss'],
})
export class CrmCustomObjectCreatedModifiedPanelComponent extends CommonTriggerPanelComponent implements OnInit {
  action = ActionComparison.Any;
  comparison = ActionComparison;
  nameKey = WORKFLOW_TRIGGERS?.[CRMCustomObjectCreatedOrModified].name;
  labelDisplay = WORKFLOW_TRIGGERS?.[CRMCustomObjectCreatedOrModified].labelDisplay;
  onlyStandardFields = false;

  supportedFieldTypes = supportedSelectionFieldTypes;
  crmCustomObjectTypeSelectorService = inject(CrmCustomObjectTypeSelectorService);
  crmFieldsSelectorService = inject(CrmFieldsSelectorService);

  customObjectTypeFormControl = new FormControl<string | null>(null);
  fieldFormControl = new FormControl<string[] | undefined>([]);

  customObjectTypeFormValue = toSignal(this.customObjectTypeFormControl.valueChanges);
  previousCustomObjectTypeFormValue = '';
  hasObjectTypeSelected = computed(() => !!this.customObjectTypeFormValue());
  rerenderReplacementChipsTrigger = signal(true);

  constructor(private crmCreatedModifiedSelectorService: CrmCreatedModifiedSelectorService) {
    super();

    effect(() => {
      const customObjectTypeFormValue = this.customObjectTypeFormValue();
      if (this.previousCustomObjectTypeFormValue) {
        this.fieldFormControl.setValue([]);
      }
      this.previousCustomObjectTypeFormValue = customObjectTypeFormValue ?? '';
      this.rerenderReplacementChipsTrigger.set(false);
      setTimeout(() => {
        this.rerenderReplacementChipsTrigger.set(true);
      });
    });
  }

  ngOnInit() {
    super.ngOnInit();
    if (this.automation.isTemplate) {
      this.onlyStandardFields = true;
    }

    const jsonData = this.step ? JSON.parse(this.step?.data ?? '{}') : {};
    this.customObjectTypeFormControl.setValue(jsonData.user_provided_custom_object_type_id ?? null);

    if (!this.triggerChoices?.rules || (this.triggerChoices.rules || []).length === 0) {
      return;
    }
    this.action = this.crmCreatedModifiedSelectorService.findActionComparison(this.triggerChoices.rules[0]);
    this.fieldFormControl.setValue(
      this.crmCreatedModifiedSelectorService.findFieldChangedValue(this.triggerChoices.rules[0]) || [],
    );
  }

  fillRuleValues(_: RuleInterface[]): RuleInterface[] {
    return [this.changeTypeRule().toRuleInterface()];
  }

  getBaseRules(): RuleInterface[] {
    return [this.changeTypeRule().toRuleInterface()];
  }

  private changeTypeRule(): Rule {
    switch (this.action) {
      case ActionComparison.Created:
        return new And([new StringEquals(changeTypeVariablePath, CREATED)]);
      case ActionComparison.Modified:
        return new And([
          new StringEquals(changeTypeVariablePath, UPDATED),
          new StringListContains(fieldsChangedVariablePath, this.fieldFormControl.value),
        ]);
      default:
        return new Or([
          new StringEquals(changeTypeVariablePath, CREATED),
          new StringListContains(fieldsChangedVariablePath, this.fieldFormControl.value),
        ]);
    }
  }

  getData() {
    const jsonData = this.step ? JSON.parse(this.step?.data ?? '{}') : {};
    jsonData.user_provided_custom_object_type_id = this.customObjectTypeFormControl.value;
    return jsonData;
  }

  getControlsToValidateOnSave(): (UntypedFormControl | UntypedFormGroup | UntypedFormArray)[] {
    return [this.fieldFormControl, this.customObjectTypeFormControl];
  }

  isFormValid(): boolean {
    return this.fieldFormControl.valid && this.customObjectTypeFormControl.valid;
  }

  finishedLoading(): void {
    this.savingEnabled$.next(true);
  }
}
